import React, {useCallback, useEffect, useState} from 'react';
import './App.css';
import {
  AppBar,
  Box, Card,
  Grid, IconButton,
  Link,
  MenuItem,
  Select,
  Table, TableBody,
  TableCell, TableHead, TableRow,
  TextField,
  TextFieldProps, Toolbar, Tooltip,
  Typography
} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {Info} from "@mui/icons-material";
import {rentalRates, statesWithCounties} from "./data/rents2022";
import {theme} from "./theme";
const mortgageCalculator = require('mortgage-calculator');

var LoanCalc = require('loan-calc');



type OnChangeParams = { target: { value: string; }; };

const useStyles = makeStyles(() => ({
  smallText: {
    fontSize: '11px'
  },
  noMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  }
}))


const InfoTooltip = ({title}: { title: string }) => {
  return <Tooltip title={title} enterTouchDelay={0} leaveTouchDelay={2500}>
    <IconButton size={"small"} style={{height: 16}}>
      <Info/>
    </IconButton>
  </Tooltip>;
}

type AmortizationRow = { "period": number; "amortizationInterest": number; "amortizationCapital": number; "remainingCapital": number };

function App() {
  const classes = useStyles()
  const textFieldProps: TextFieldProps = {
    variant: 'outlined',
    style: {marginBottom: 8}
  }
  const [zip, setZip] = useState('40515')
  const onZipChange = useCallback((e: OnChangeParams) => {
    const {value} = e.target;
    setZip(value)
  }, [setZip])

  const [purchasePrice, setPurchasePrice] = useState(200000)
  const onPurchasePriceChange = useCallback((e: OnChangeParams) => {
    setPurchasePrice(Number(e.target.value))
  }, [setPurchasePrice])

  const [closingCostsPercent, setClosingCostsPercent] = useState(2)
  const onClosingCostsPercentChange = useCallback((e: OnChangeParams) => {
    setClosingCostsPercent(Number(e.target.value))
  }, [setClosingCostsPercent])

  const [downPaymentPercent, setDownPaymentPercent] = useState(20)
  const onDownPaymentPercentChange = useCallback((e: OnChangeParams) => {
    setDownPaymentPercent(Number(e.target.value))
  }, [setDownPaymentPercent])

  const [loanInterestPercent, setLoanInterestPercent] = useState(5)
  const [loanInterestPercentStringValue, setLoanInterestPercentStringValue] = useState('5')
  const onLoanInterestPercentChange = useCallback((e: OnChangeParams) => {
    setLoanInterestPercentStringValue(e.target.value)
    setLoanInterestPercent(Number(e.target.value))
  }, [setLoanInterestPercent, setLoanInterestPercentStringValue])

  const [loanTerm, setLoanTerm] = useState(30)
  const onLoanTermChange = useCallback((e: OnChangeParams) => {
    setLoanTerm(Number(e.target.value))
  }, [setLoanTerm])

  const [grossMonthlyRent, setGrossMonthlyRent] = useState(2000)
  const onGrossMonthlyRentChange = useCallback((e: OnChangeParams) => {
    setGrossMonthlyRent(Number(e.target.value))
  }, [setGrossMonthlyRent])

  const [numberOfUnits, setNumberOfUnits] = useState(2)
  const onNumberOfUnitsChange = useCallback((e: OnChangeParams) => {
    setNumberOfUnits(Number(e.target.value))
  }, [setNumberOfUnits])

  const [annualPropertyTaxes, setAnnualPropertyTaxes] = useState((.0095 * purchasePrice))
  const onAnnualPropertyTaxesChange = useCallback((e: OnChangeParams) => {
    setAnnualPropertyTaxes(Number(e.target.value))
  }, [setAnnualPropertyTaxes])

  const [annualInsurance, setAnnualInsurance] = useState(1000)
  const onAnnualInsuranceChange = useCallback((e: OnChangeParams) => {
    setAnnualInsurance(Number(e.target.value))
  }, [setAnnualInsurance])

  const [vacancyRate, setVacancyRate] = useState(5)
  const onVacancyRateChange = useCallback((e: OnChangeParams) => {
    setVacancyRate(Number(e.target.value))
  }, [setVacancyRate])

  const [maintenanceRate, setMaintenanceRate] = useState(5)
  const onMaintenanceRateChange = useCallback((e: OnChangeParams) => {
    setMaintenanceRate(Number(e.target.value))
  }, [setMaintenanceRate])

  const [capitalExpRate, setCapitalExpRate] = useState(5)
  const onCapitalExpRateChange = useCallback((e: OnChangeParams) => {
    setCapitalExpRate(Number(e.target.value))
  }, [setCapitalExpRate])

  const [propertyManagementRate, setPropertyManagementRate] = useState(0)
  const onPropertyManagementRateChange = useCallback((e: OnChangeParams) => {
    setPropertyManagementRate(Number(e.target.value))
  }, [setPropertyManagementRate])

  const [state, setState] = useState('KY')
  const onStateChange = useCallback((e: OnChangeParams) => {
    setState(e.target.value)
  }, [setState])

  const [county, setCounty] = useState('Fayette County')
  const onCountyChange = useCallback((e: OnChangeParams) => {
    setCounty(e.target.value)
  }, [setCounty])

  const [appreciatetionRate, setAppreciatetionRate] = useState(3)
  const onAppreciatetionRateChange = useCallback((e: OnChangeParams) => {
    setAppreciatetionRate(Number(e.target.value))
  }, [setAppreciatetionRate])

  useEffect(() => {
    // @ts-ignore
    if (statesWithCounties[state].includes(county)) return;
    // @ts-ignore
    setCounty(statesWithCounties[state][0])
  }, [state])

  const [showRents, setShowRents] = useState(false);

  useEffect(() => {
    if (!loanTerm) return;
    getCurrentRate(loanTerm)
      .then((res) => {
        setLoanInterestPercent(res.rates.rateQuery.apr)
        setLoanInterestPercentStringValue(res.rates.rateQuery.apr)
      })
  }, [loanTerm])

  function calculatePandI() {
    if (!amount || !purchasePrice) return 0
    return LoanCalc.paymentCalc({
      amount,
      rate: loanInterestPercent,
      termMonths: (loanTerm * 12)
    })
  }


  let downPayment = (downPaymentPercent / 100) * purchasePrice;
  let amount = (purchasePrice - downPayment)
  let pandI = calculatePandI();
  const getAmortization = () => {
    return mortgageCalculator.amortization(amount, pandI, loanTerm * 12, (loanInterestPercent / 100))
    // purchasePrice - downPayment, calculatePandI(), loanTerm * 12, loanInterestPercent
  }
  let monthlyTaxes = annualPropertyTaxes / 12;
  let monthlyInsurance = annualInsurance / 12;

  function getFormat(number: number) {
    return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(number);
  }

  let vacancyExpense = (vacancyRate / 100) * grossMonthlyRent;
  const cashNeeded = downPayment + (purchasePrice * (closingCostsPercent / 100))
  let onePercentRule = (grossMonthlyRent / purchasePrice) * 100;
  let maintenanceExpense = (maintenanceRate / 100) * grossMonthlyRent;
  let propertyManagementExpense = (propertyManagementRate / 100) * grossMonthlyRent;
  let capitalExp = (capitalExpRate / 100) * grossMonthlyRent;
  let monthlyExpenses = pandI + monthlyTaxes + monthlyInsurance + vacancyExpense + capitalExp + maintenanceExpense + propertyManagementExpense;
  let netCashFlow = grossMonthlyRent - monthlyExpenses;
  let cashFlowPerDoor = netCashFlow / numberOfUnits;
  const cashOnCashReturn = (netCashFlow * 12) / cashNeeded
  let netOperatingIncome = (netCashFlow + pandI + capitalExp) * 12;
  let capRate = netOperatingIncome / purchasePrice;
  let rents = rentalRates?.find((rate) => {
    const countyOrTownName = rate.county_town_name || rate.countyname;
    return (countyOrTownName === county && (rate?.state_alpha === state))
  });
  const toggleShowRents = () => setShowRents(!showRents);

  function onKeyDown(e: { keyCode: number; }) {
    if (e.keyCode === 38) {
      setPurchasePrice(purchasePrice + 5000)
    } else if (e.keyCode === 40) {
      setPurchasePrice(purchasePrice - 5000)
    }
  }

  const green = theme.palette.success.light;
  const yellow = theme.palette.warning.light;
  const red = theme.palette.error.light;
  const everyFiveYears = (amortizationRow: AmortizationRow) => amortizationRow.period === 1 || amortizationRow.period % 60 === 0;
  const amortizationTableRow = (amortizationRow: AmortizationRow, i: number) => {
    const monthlyAppreciationRate = 1 + ((appreciatetionRate / 100) / 12);
    const homeValue = purchasePrice * Math.pow(monthlyAppreciationRate, amortizationRow.period);
    const homeEquity = homeValue - amortizationRow.remainingCapital;
    return (
      <TableRow key={`row-${i}`}>
        <TableCell>{amortizationRow.period}</TableCell>
        <TableCell>{getFormat(amortizationRow.amortizationInterest)}</TableCell>
        <TableCell>{getFormat(amortizationRow.amortizationCapital)}</TableCell>
        <TableCell>{getFormat(amortizationRow.remainingCapital)}</TableCell>
        <TableCell>{getFormat(homeValue)}</TableCell>
        <TableCell>{getFormat(homeEquity)}</TableCell>
      </TableRow>
    );
  };
  return (
      <Box className="App">
        {!window.location.href.includes('https://freeresearch.net/free-investment-property-analysis-tool') && <AppBar position="static" style={{background: theme.palette.common.black, marginBottom: 4}}>
          <Toolbar>
            <Link style={{cursor: 'pointer', textDecoration: 'none', color: theme.palette.common.white}}
                  href={'https://freeresearch.net'} target={'_blank'} fontFamily={'roboto'} variant="h6"
                  component="div">FREE RESEARCH</Link>
          </Toolbar>
        </AppBar>}
        <Typography variant={'h1'} fontSize={36}>Investment Property Analysis Tool</Typography>
        <Card style={{ padding: 36, marginBottom: 8 }}>
          <Typography variant={'h2'} fontSize={24}>How To Use This Tool</Typography><br/>
          <Typography align={'left'}>
            Enter the relevant inputs into the form -- provide your estimated purchase price, estimated closing cost %,
            down payment %, mortgage term, mortgage interest rate (Current mortgage rates are populated on page load and
            when you change mortgage type. Additionally, there is a lookup tool provided), estimated gross monthly rents
            (use the rent lookup tool to view HUD data on fair market rents in the relevant area), number of units,
            property taxes (property tax rate lookup tool opens in a new tab), and insurance.
          </Typography><br/>
          <Typography align={'left'}>
            Adjust your estimated monthly expenses (as a % of gross revenue) for vacancies, maintenance and repairs, capital expenditures,
            and property management.
          </Typography><br/>
          <Typography align={'left'}>
            View your results in the analysis tool. Green values indicate the property may be a suitable investment based on that metric, given your inputs.
            Red values may indicate that the property is not suitable for investment at the current purchase price. If you see red values, try
            using the arrow keys to decrease your purchase price in $5,000 increments until green values begin to appear.
          </Typography>
          <Typography variant={'h3'} fontSize={20} mt={4}>About this site</Typography>
          <Typography align={'left'} fontWeight={'bold'}>FreeResearch.net was established in 2022 to provide real estate investors with valuable, high quality analysis tools and calculators.
            This tool is fully launched and is not under construction.
            The intended user is an American real estate investor who is in the process of screening potential investments.
            This is the only free investment real estate calculator on the web that offers FreeResearch.net's sleek, patent pending user interface, complete with financial analysis and embedded lookup tools for mortgage rates, fair market rents, and property tax rates.
            As such, this tool is unique and creates added value for real estate investors. It is high quality content and a fully functional page and meets all Google AdSense policies.
          </Typography>
        </Card>
        <Grid container direction={'row'} justifyContent={'space-around'} width={'100%'}>
          <Grid container item direction={'column'} lg={5} xs={5} justifyContent={'flex-start'} display={'flex'}
                paddingTop={1}>
            <Typography variant={'h2'} fontSize={24} align={'left'}>Costs</Typography>
            <TextField {...textFieldProps} label='Zip Code' value={zip} onChange={onZipChange}/>
            <TextField {...textFieldProps} label="Purchase Price ($)" value={purchasePrice}
                       onChange={onPurchasePriceChange} onKeyDown={onKeyDown}/>
            <TextField {...textFieldProps} label={'Closing Costs (%)'} value={closingCostsPercent}
                       onChange={onClosingCostsPercentChange}/>

            <Typography variant={'h2'} fontSize={24} mt={4} align={'left'}>Financing</Typography>
            <TextField {...textFieldProps} label={'Down Payment (%)'} value={downPaymentPercent}
                       onChange={onDownPaymentPercentChange}/>
            {/* @ts-ignore */}
            <Select {...textFieldProps} labelId={'loanTermLabel'} defaultValue={'30'} value={`${loanTerm}`}
                    onChange={onLoanTermChange}>
              <MenuItem value={'15'}>15 Year Fixed</MenuItem>
              <MenuItem value={'30'}>30 Year Fixed</MenuItem>
            </Select>
            <TextField {...textFieldProps} label={'Loan Interest Rate (%)'} value={loanInterestPercentStringValue}
                       onChange={onLoanInterestPercentChange}/>
            <Link
              marginTop={-1}
              fontSize={'14px'}
              align={'left'}
              target={'_blank'}
              href={`https://www.zillow.com/mortgage-rates/?value=${purchasePrice}&down=${purchasePrice * (downPaymentPercent / 100)}&auto=true&va=false`}
            >
              View Current Rates
            </Link>

            <Typography variant={'h2'} fontSize={24} mt={4} align={'left'}>Revenue Assumptions</Typography>
            <TextField {...textFieldProps} label="Gross Monthly Rent ($)" value={grossMonthlyRent}
                       onChange={onGrossMonthlyRentChange}/>
            <Link
              marginTop={-1}
              marginBottom={1}
              align={'left'}
              target={'_blank'}
              onClick={toggleShowRents}
              fontSize={'14px'}
              style={{ cursor: 'pointer' }}
            >
              {!showRents ? 'Show Rent Lookup' : 'Hide Rent Lookup'}
            </Link>
            {showRents && <Card style={{ marginBottom: 18 }}>
              <Select value={state} label={'State'} onChange={onStateChange}>
                {Object.keys(statesWithCounties).map((stateAbbreviation) => {
                  return <MenuItem value={stateAbbreviation}>{stateAbbreviation}</MenuItem>
                })}
              </Select>
              <Select value={county} label={'County'} onChange={onCountyChange}>
                {/* @ts-ignore */}
                {(statesWithCounties[state]).sort().map((countyName: string) => {
                  return <MenuItem value={countyName}>{countyName.replace(' town', '')}</MenuItem>
                })}
              </Select>

              {rents && <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>Studio</TableCell>
                    <TableCell>{getFormat(rents.fmr_0)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>1 BR</TableCell>
                    <TableCell>{getFormat(rents.fmr_1)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>2 BR</TableCell>
                    <TableCell>{getFormat(rents.fmr_2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>3 BR</TableCell>
                    <TableCell>{getFormat(rents.fmr_3)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>4 BR</TableCell>
                    <TableCell>{getFormat(rents.fmr_4)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>}
              <Link fontSize={11} href={'https://www.huduser.gov/portal/datasets/fmr.html#2022_data'} target={'_blank'}>Source</Link>
            </Card>}


            <TextField {...textFieldProps} label="Number of Units" value={numberOfUnits}
                       onChange={onNumberOfUnitsChange}/>

            <Typography variant={'h2'} fontSize={24} mt={4} align={'left'}>Annual Expenses</Typography>
            <TextField {...textFieldProps} label="Property Taxes" value={annualPropertyTaxes}
                       onChange={onAnnualPropertyTaxesChange}/>
            <Link
              marginTop={-1}
              marginBottom={1}
              align={'left'}
              target={'_blank'}
              href={`https://smartasset.com/taxes/property-taxes?location=${zip}`}
              fontSize={'14px'}
            >
              Lookup Property Tax Rates
            </Link>
            {/*`https://smartasset.com/taxes/property-taxes?location=${zip}`*/}
            <TextField {...textFieldProps} label="Insurance" value={annualInsurance} onChange={onAnnualInsuranceChange}/>
          </Grid>

          <Grid container item direction={'row'} lg={6} xs={6} alignItems={'flex-start'} justifyContent={'space-between'}
                padding={1}>
            <Grid item container direction={'column'} lg={4}>
              <Typography variant={'h2'} fontSize={24}>Monthly Expenses</Typography>
              <Card>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>Principal and Interest</TableCell>
                      <TableCell>{getFormat(pandI)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Taxes</TableCell>
                      <TableCell>{getFormat(monthlyTaxes)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Insurance</TableCell>
                      <TableCell>{getFormat(monthlyInsurance)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.smallText}>
                        <input
                          style={{display: 'inline', width: '16px'}}
                          value={vacancyRate}
                          onChange={onVacancyRateChange}/>%
                        <br/>
                        Vacancy Rate
                      </TableCell>
                      <TableCell>{getFormat(vacancyExpense)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.smallText}>
                        <input
                          style={{display: 'inline', width: '16px'}}
                          value={maintenanceRate}
                          onChange={onMaintenanceRateChange}/>%
                        <br/>
                        Maintenance and Repairs
                      </TableCell>
                      <TableCell>{getFormat(maintenanceExpense)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.smallText}>
                        <input
                          style={{display: 'inline', width: '16px'}}
                          value={capitalExpRate}
                          onChange={onCapitalExpRateChange}/>%
                        <br/>
                        Capital Expenditures
                      </TableCell>
                      <TableCell>{getFormat(capitalExp)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.smallText}>
                        <input style={{display: 'inline', width: '16px'}}
                          value={propertyManagementRate}
                          onChange={onPropertyManagementRateChange}/>%
                        <br/>
                        Property Management
                      </TableCell>
                      <TableCell>{getFormat(propertyManagementExpense)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell><strong>{getFormat(monthlyExpenses)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
            </Grid>
            <Grid item container direction={'column'} lg={7}>
              <Typography variant={'h2'} fontSize={24}>Analysis</Typography>
              <Card>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        Cash Needed
                        <InfoTooltip title={'Down Payment + Closing Costs'}/>
                      </TableCell>
                      <TableCell>
                        {getFormat(cashNeeded)}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell style={{fontSize: 13}}>
                        Monthly Net Cash Flow
                        <InfoTooltip title={"Gross Monthly Rent - Monthly Expenses"}/>
                      </TableCell>
                      <TableCell>
                        <Typography color={netCashFlow < 0 ? red : green}>{getFormat(netCashFlow)}</Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>
                        Net Cash Flow Per Door
                      </TableCell>
                      <TableCell>
                        <Typography
                          color={cashFlowPerDoor > 225 ? green : cashFlowPerDoor > 150 ? yellow : red}>{getFormat(cashFlowPerDoor)}</Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>
                        Cash on Cash Return
                        <InfoTooltip title={"Annual Net Cash Flow / Cash Needed"}/>
                      </TableCell>
                      <TableCell>
                        <Typography
                          color={cashOnCashReturn > .12 ? green : cashOnCashReturn > .08 ? yellow : red}>{(cashOnCashReturn * 100).toFixed(2)}%</Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell style={{fontSize: 13}}>
                        Net Operating Income
                        <InfoTooltip
                          title={"Annual Gross Rents - Annual Operating Expenses (Excludes principal and interest payments on loans, capital expenditures, depreciation, and amortization)"}/>
                      </TableCell>
                      <TableCell>
                        {getFormat(netOperatingIncome)}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>
                        1% Rule
                        <InfoTooltip
                          title={'The 1% rule of real estate investing measures the price of the investment property against the gross income it will generate. For a potential investment to pass the 1% rule, its monthly rent must be equal to or no less than 1% of the purchase price.'}/>
                      </TableCell>
                      <TableCell>
                        <Typography
                          color={onePercentRule >= 1 ? green : red}>{onePercentRule.toFixed(2)}%</Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>
                        Purchase Cap Rate
                        <InfoTooltip title={'Net Operating Income / Purchase Price'}/>
                      </TableCell>
                      <TableCell>
                        <Typography
                          color={capRate > .07 ? green : capRate > .05 ? yellow : red}>{(capRate * 100).toFixed(2)}%</Typography>
                      </TableCell>
                    </TableRow>

                  </TableBody>
                </Table>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid className={classes.noMobile} item marginTop={8}>
          <Card style={{ padding: 8 }}>
            <Typography variant={'h2'} fontSize={24}>Amortization and Long Term Equity</Typography><br/>
            <Grid container justifyContent={'center'} alignItems={'center'}>
              <input value={appreciatetionRate} onChange={onAppreciatetionRateChange} style={{ width: '30px' }} />% Annual Appreciation Rate
            </Grid>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Amortization Interest</TableCell>
                  <TableCell>Amortization Principal</TableCell>
                  <TableCell>Remaining Principal</TableCell>
                  <TableCell>Home Value</TableCell>
                  <TableCell>Equity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getAmortization().filter(everyFiveYears).map(amortizationTableRow)}
              </TableBody>
            </Table>

          </Card>
        </Grid>
      </Box>
  );
}

// const getRentData = async (zip: string) => {
//   return await fetch('https://www.rentdata.org/lookup?zip=' + zip, {
//     method: "GET",
//
//   }).then(async (res) => console.log(res))
// }

const getCurrentRate = async (term: number) => {
  return await fetch(`https://mortgageapi.zillow.com/getCurrentRates?partnerId=RD-CZMBMCZ&queries.rateQuery.program=Fixed${term}Year&queries.rateQuery.loanAmountBucket=Conforming&queries.rateQuery.loanToValueBucket=Normal&queries.rateQuery.creditScoreBucket=High`, {
    method: "GET",

  }).then(async (res) => await res.json())
}
export default App;
